import React, { forwardRef, useContext, useEffect, useRef } from 'react';
import _merge from 'lodash/merge';
import _delay from 'lodash/delay';
import { getEasing, getDuration, getContext, isAnimationDisabled } from 'common';
import { useSpring, animated } from 'react-spring';
import { SPEED_NORMAL, SPEED_SLOW } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { OPACITY_0 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/opacity';
import { TRANSITION_STANDARD } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import { StatusIndicatorContext, STATUS } from './StatusIndicator';
import styled from './styles';

export interface StatusIndicatorClearedOverlayProps extends React.ComponentPropsWithRef<'div'> {
  /** Render icon for cleared overlay. */
  icon?: React.ReactNode;
  /** Text content of cleared overlay. */
  text?: string;
  /** Props just for the `StatusIndicatorText` wrapper which is the wrapper of text content */
  textWrapperProps?: {};
  /** Control the background animation config for ClearedOverlay wrapper. This uses react-spring transitions config. See https://react-spring.io for more information. */
  backgroundAnimationProps?: any;
  /** Control the icon and text width and opacity animation config for OverlyContent wrapper. This uses react-spring transitions config. See https://react-spring.io for more information. */
  contentAnimationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** To customize the wrapper style. */
  style?: React.CSSProperties
}

/**
 * StatusIndicatorClearedOverlay component.
 * Any other props than explicitly defined props will be provided to the root element.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference).
 */

const StatusIndicatorClearedOverlay = forwardRef<HTMLElement, StatusIndicatorClearedOverlayProps>((props: StatusIndicatorClearedOverlayProps, ref) => getContext(({ disableAnimation }) => {
  const {
    icon,
    text,
    backgroundAnimationProps,
    contentAnimationProps: passedContentAnimationProps,
    disableAnimation: propsdisableAnimation,
    style,
    textWrapperProps,
    ...otherProps
  } = props;
  const { $theme, status } = useContext(StatusIndicatorContext);

  const { default: defaultStatus, cleared, warning } = STATUS;

  const lastStatus = useRef(status);

  const [clearedBgAnimationProps, setBgAnimation] = useSpring(() =>
    backgroundAnimationProps || {
      from: { width: '0%', opacity: OPACITY_0 },
      config: {
        duration: getDuration(SPEED_SLOW),
        easing: getEasing(TRANSITION_STANDARD)
      },
      immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation),
    });

  const [contentAnimationProps, setContentAnimation] = useSpring(() =>
    passedContentAnimationProps || {
      from: { transform: 'scale(0)', opacity: OPACITY_0 },
      delay: 150,
      config: {
        duration: getDuration(SPEED_NORMAL),
        easing: getEasing(TRANSITION_STANDARD),
        tension: 380,
        friction: 15
      },
      immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation),
    });

  useEffect(() => {
    // if the status changes to cleared, trigger the animation.
    if (lastStatus.current !== cleared && status === cleared) {
      // @ts-expect-error: This might be fixed after updating react-spring
      setBgAnimation({ width: '120%', opacity: 1 });
      // @ts-expect-error: This might be fixed after updating react-spring
      setContentAnimation({ transform: 'scale(1)', opacity: 1 });
    }
    // if the status changes from cleared to others, trigger the animation based on conditions.
    if (lastStatus.current === cleared) {
      if (status === warning) {
        // @ts-expect-error: This might be fixed after updating react-spring
        setBgAnimation({ width: '120%', opacity: OPACITY_0 });
        _delay(() => {
          // @ts-expect-error: This might be fixed after updating react-spring
          setBgAnimation({ width: '10%', opacity: OPACITY_0 });
          // @ts-expect-error: This might be fixed after updating react-spring
          setContentAnimation({ transform: 'scale(0)', opacity: OPACITY_0 });
        }, 300);
      }
      if (status === defaultStatus) {
        // @ts-expect-error: This might be fixed after updating react-spring
        setContentAnimation({ transform: 'scale(0)', opacity: OPACITY_0 });
        _delay(() => {
          // @ts-expect-error: This might be fixed after updating react-spring
          setBgAnimation({ width: '10%', opacity: OPACITY_0 });
        }, 300);
      }
    }
    lastStatus.current = status;
  }, [status]);

  const resolveStyles = _merge({}, clearedBgAnimationProps, style);

  return (
    <styled.ClearedOverlay
      as={animated.div}
      ref={ref}
      $theme={$theme}
      status={status}
      style={resolveStyles}
      {...otherProps}
    >
      <styled.OverlyContent as={animated.div} style={isAnimationDisabled(propsdisableAnimation, disableAnimation) ? undefined : contentAnimationProps}>
        {!!icon && <styled.IconWrapper>{icon}</styled.IconWrapper>}
        {!!text && (
          <styled.StatusIndicatorText $theme={$theme} {...textWrapperProps}>
            {text}
          </styled.StatusIndicatorText>
        )}
      </styled.OverlyContent>
    </styled.ClearedOverlay>
  );
}));
StatusIndicatorClearedOverlay.displayName = 'StatusIndicatorClearedOverlay';

StatusIndicatorClearedOverlay.defaultProps = {
  textWrapperProps: {},
  backgroundAnimationProps: undefined,
  contentAnimationProps: undefined,
  disableAnimation: undefined,
  icon: undefined,
  text: '',
  style: {}
};

export default StatusIndicatorClearedOverlay;
